import { useAuth } from '../../context/AuthContext';

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  roles: string[];
  badge?: string;
  group: 'workspace' | 'catalog' | 'operations' | 'marketing' | 'system';
}

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isOpen: boolean;
  onCloseMobile: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export const NAVIGATION_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊', roles: ['admin', 'executive', 'manager', 'sales_rep', 'accountant'], group: 'workspace' },
  { id: 'quotations', label: 'Quotations', icon: '📄', roles: ['admin', 'executive', 'manager', 'sales_rep', 'accountant'], group: 'workspace' },
  { id: 'parties', label: 'Customer Parties', icon: '👥', roles: ['admin', 'executive', 'manager', 'sales_rep', 'marketing'], group: 'workspace' },
  { id: 'companies', label: 'Company Entities', icon: '🏢', roles: ['admin', 'executive', 'manager', 'sales_rep', 'accountant'], group: 'workspace' },
  { id: 'products', label: 'Product Catalog', icon: '⚙', roles: ['admin', 'executive', 'manager'], group: 'catalog' },
  { id: 'pricelist', label: 'Price List', icon: '🏷', roles: ['admin', 'executive', 'manager'], group: 'catalog' },
  { id: 'lattice', label: 'Lattice Calculator', icon: '🏗', roles: ['admin', 'executive', 'manager', 'sales_rep'], group: 'catalog', badge: 'VBA' },
  { id: 'reports', label: 'Reports & Analytics', icon: '📈', roles: ['admin', 'executive', 'manager', 'accountant'], group: 'operations' },
  { id: 'import', label: 'Bulk Import', icon: '📥', roles: ['admin', 'executive', 'manager'], group: 'operations' },
  { id: 'stock', label: 'Stock Inventory', icon: '📦', roles: ['admin', 'executive', 'manager'], group: 'operations', badge: 'VBA' },
  { id: 'team', label: 'Team Directory', icon: '👥', roles: ['admin', 'executive', 'manager'], group: 'system' },
  { id: 'tracker', label: 'Project Tracker', icon: '🚀', roles: ['admin', 'executive', 'manager', 'sales_rep', 'accountant'], group: 'system' },
  { id: 'help', label: 'User Guide & SOP', icon: '📖', roles: ['admin', 'executive', 'manager', 'sales_rep', 'accountant', 'marketing'], group: 'system' },
];

export function Sidebar({ currentPage, onNavigate, isOpen, onCloseMobile, isCollapsed, onToggleCollapse }: SidebarProps) {
  const { user, profile, signOut, isExecutive } = useAuth();
  const rawRole = profile?.role || 'sales_rep';
  const effectiveRole = isExecutive ? (rawRole === 'admin' ? 'admin' : 'executive') : rawRole;
  const visibleItems = NAVIGATION_ITEMS.filter(item => item.roles.includes(effectiveRole) || (isExecutive && item.roles.includes('executive')));

  const groups = [
    { key: 'workspace', label: 'Core Workspace' },
    { key: 'catalog', label: 'Catalog & Rates' },
    { key: 'operations', label: 'Operations & Data' },
    { key: 'system', label: 'System & Support' },
  ];

  const handleSignOut = async () => {
    try { await signOut(); } catch (err) { console.error('Sign out error:', err); }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'executive': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'manager': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'accountant': return 'bg-cyan-100 text-cyan-700 border-cyan-200';
      case 'marketing': return 'bg-pink-100 text-pink-700 border-pink-200';
      default: return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    }
  };


  const getUserInitials = () => {
    if (profile?.name) return profile.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
    if (user?.email) return user.email.slice(0, 2).toUpperCase();
    return 'SK';
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={onCloseMobile} />
      )}

      <aside className={`fixed top-0 bottom-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 transition-all duration-200 ${isCollapsed ? 'w-20' : 'w-64'} ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Brand Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-gray-100">
          <div onClick={() => onNavigate('dashboard')} className="flex items-center gap-2.5 cursor-pointer select-none">
            <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">⚙</div>
            {!isCollapsed && (
              <div className="flex flex-col truncate">
                <span className="font-bold text-sm text-gray-900 leading-tight">SAI KRISHNA</span>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider leading-none mt-0.5">INDUSTRIES · PORTAL</span>
              </div>
            )}
          </div>
          <button onClick={onToggleCollapse} className="hidden lg:flex p-1 text-gray-400 hover:text-gray-600 rounded" title={isCollapsed ? 'Expand' : 'Collapse'}>
            <span className="text-xs">{isCollapsed ? '▶' : '◀'}</span>
          </button>
          <button onClick={onCloseMobile} className="lg:hidden p-1 text-gray-400 hover:text-gray-600">✕</button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-5">
          {groups.map(group => {
            const groupItems = visibleItems.filter(item => item.group === group.key);
            if (groupItems.length === 0) return null;
            return (
              <div key={group.key} className="space-y-0.5">
                {!isCollapsed && (
                  <div className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 select-none">{group.label}</div>
                )}
                {groupItems.map(item => {
                  const isActive = currentPage === item.id || currentPage.startsWith(`${item.id}-`);
                  return (
                    <button
                      key={item.id}
                      data-nav={item.id}
                      aria-label={item.label}
                      onClick={() => { onNavigate(item.id); onCloseMobile(); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'} ${isCollapsed ? 'justify-center px-2' : ''}`}
                      title={item.label}
                    >
                      <span className="text-base flex-shrink-0">{item.icon}</span>
                      {!isCollapsed && <span className="truncate flex-1 text-left">{item.label}</span>}
                      {!isCollapsed && item.badge && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-600">{item.badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Admin Quick Switch (Only for Admins) */}
        {(effectiveRole === 'admin' || isExecutive) && (
          <div className="px-3 pt-2 pb-1">
            <button
              onClick={() => onNavigate('admin')}
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-slate-900 to-indigo-950 text-amber-300 hover:from-black hover:to-indigo-900 border border-amber-500/30 transition-all shadow-sm ${
                isCollapsed ? 'justify-center px-1.5' : ''
              }`}
              title="Open Admin Command Center (/admin)"
            >
              <span className="text-sm">🛡️</span>
              {!isCollapsed && (
                <span className="truncate flex-1 text-left">Admin Console</span>
              )}
              {!isCollapsed && (
                <span className="text-[9px] px-1 py-0.2 rounded bg-amber-400/20 text-amber-300 border border-amber-400/40">/admin</span>
              )}
            </button>
          </div>
        )}

        {/* User Profile */}
        <div className="p-3 border-t border-gray-100">
          <div className={`flex items-center gap-2.5 p-2 rounded-lg bg-gray-50 ${isCollapsed ? 'justify-center p-1.5' : ''}`}>
            <div className="h-8 w-8 rounded-full bg-blue-600 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">{getUserInitials()}</div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-900 truncate">{profile?.name || user?.email?.split('@')[0] || 'User'}</div>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.1 rounded border ${getRoleBadgeColor(effectiveRole)}`}>
                  {effectiveRole.replace('_', ' ')}
                </span>

              </div>
            )}
            <button onClick={handleSignOut} data-testid="sidebar-sign-out-btn" className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors" title="Sign out" aria-label="Sign out">🚪</button>
          </div>
        </div>
      </aside>
    </>
  );
}
